/**
 * Roles and permissions (plan 02 §4.4). The clinic matrix is the mockup `CAN`
 * matrix (docs/mockup-inventory.md §3.4), the same for every clinic. Roles are
 * Cognito groups; this file is the only place that says what a role may do.
 */

export const CLINIC_ROLES = ['Owner', 'Manager', 'Clinician', 'FrontDesk', 'Viewer'] as const;
export type ClinicRole = (typeof CLINIC_ROLES)[number];

export const STAFF_ROLES = ['PlatformAdmin', 'Support', 'Reviewer', 'Finance', 'Viewer'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

export const CLINIC_PERMISSIONS = [
  'dashboard.view',
  'patients.view',
  'conversations.respond',
  'leads_tasks.manage',
  'services.edit',
  'laura.configure',
  'teach.suggest',
  'testbench.use',
  'channels.connect',
  'team.manage',
  'team.manage_owners',
  'billing.manage',
  'reports.export',
  'audit.view',
  'products.view',
  'products_contacts.edit',
  'photos.review',
  // Epic 06 (plan 06 §10): book, move and cancel for patients from the portal.
  'bookings.manage',
  // Epic 09 (ADR 086): write and send marketing campaigns.
  'marketing.manage',
  // Epic 10 (plan 10 §13 item 8): export and delete one contact's data.
  'privacy.manage',
] as const;
export type ClinicPermission = (typeof CLINIC_PERMISSIONS)[number];

export const STAFF_PERMISSIONS = [
  'tenants.read',
  'tenants.write',
  'staff.manage',
  'review.decide',
  'billing.read',
  'support.access',
  'audit.view',
  // Epic 26 (ADR 152): Platform health, logs and alerts. Novations staff only.
  'platform.observe',
  // ADR 162: edit base and vertical template drafts, and publish a new version.
  'templates.edit',
  'templates.publish',
  // ADR 161: remove a clinic and all its data. PlatformAdmin only.
  'tenants.remove',
  // ADR 178: quarantined and platform phone numbers (Extend, Return, port-out, Release now, platform sender). PlatformAdmin only.
  'numbers.manage',
  // ADR 181: the reply safety allow-dictionary (Safety terms). PlatformAdmin only.
  'safety.manage',
  // ADR 196: Back office › Finance (cost, revenue, margin per clinic). PlatformAdmin and,
  // since ADR 238 (Q-06, Q-59), Finance (read only; it also reads the platform spend cap).
  'finance.view',
  // ADR 195: operational incidents (status, mute) and the Pause auto-fix switch.
  'incidents.manage',
  // ADR 197: Back office › Support requests (AI triage queue, approve, reject, resolve).
  'support.tickets',
  // ADR 198: approve or reject the investigator's security action (WAF block, user disable). PlatformAdmin only.
  'incidents.approve',
  // ADR 219: approve or reject a clinic's patient data-deletion request (Back office › Privacy requests).
  'privacy.approve',
  // ADR 238 (Q-48): the treatment catalogue's prescription marks. PlatformAdmin only.
  'catalogue.manage',
] as const;
export type StaffPermission = (typeof STAFF_PERMISSIONS)[number];

const ALL_CLINIC: readonly ClinicPermission[] = CLINIC_PERMISSIONS;
const FRONT_LINE: readonly ClinicPermission[] = [
  'dashboard.view',
  'patients.view',
  'conversations.respond',
  'leads_tasks.manage',
  'products.view',
  'bookings.manage',
];

const CLINIC_MATRIX: Record<ClinicRole, ReadonlySet<ClinicPermission>> = {
  Owner: new Set(ALL_CLINIC),
  // Manager: everything except billing and managing Owners.
  Manager: new Set(ALL_CLINIC.filter((p) => p !== 'billing.manage' && p !== 'team.manage_owners')),
  Clinician: new Set([...FRONT_LINE, 'teach.suggest', 'testbench.use', 'photos.review']),
  FrontDesk: new Set(FRONT_LINE),
  Viewer: new Set(['dashboard.view', 'reports.export']),
};

const STAFF_MATRIX: Record<StaffRole, ReadonlySet<StaffPermission>> = {
  PlatformAdmin: new Set(STAFF_PERMISSIONS),
  Support: new Set([
    'tenants.read',
    'support.access',
    'platform.observe',
    'incidents.manage',
    'support.tickets',
    'privacy.approve',
  ]),
  // ADR 238 (Q-43): Reviewers edit template drafts; only PlatformAdmin publishes.
  Reviewer: new Set(['tenants.read', 'review.decide', 'templates.edit']),
  // ADR 238 (Q-06, Q-59): the Finance page and the platform spend cap, read only.
  Finance: new Set(['tenants.read', 'billing.read', 'finance.view']),
  Viewer: new Set(['tenants.read', 'platform.observe']),
};

export function isClinicRole(value: unknown): value is ClinicRole {
  return typeof value === 'string' && (CLINIC_ROLES as readonly string[]).includes(value);
}

export function isStaffRole(value: unknown): value is StaffRole {
  return typeof value === 'string' && (STAFF_ROLES as readonly string[]).includes(value);
}

export function clinicCan(role: ClinicRole, permission: ClinicPermission): boolean {
  return CLINIC_MATRIX[role].has(permission);
}

export function staffCan(role: StaffRole, permission: StaffPermission): boolean {
  return STAFF_MATRIX[role].has(permission);
}

export function clinicPermissions(role: ClinicRole): ClinicPermission[] {
  return CLINIC_PERMISSIONS.filter((p) => CLINIC_MATRIX[role].has(p));
}

export function staffPermissions(role: StaffRole): StaffPermission[] {
  return STAFF_PERMISSIONS.filter((p) => STAFF_MATRIX[role].has(p));
}

/**
 * MFA is mandatory for every staff user and, since ADR 238 (Q-02), for every
 * clinic role: all of them see patient data or conversations. Kept as one
 * function so the authorizer, the Cognito triggers and the portal share the rule.
 */
export function clinicRoleRequiresMfa(role: ClinicRole): boolean {
  return isClinicRole(role);
}

/**
 * Plan 257 EF-08: whether a clinic token's sign-in really passed a second factor.
 * The pre-token trigger sets `mfa` ("true" when a factor is enrolled) and
 * `mfa_since` (when that factor was first seen, epoch seconds); `auth_time` is the
 * original sign-in, kept across refreshes. A factor turned on during a
 * password-only session is newer than that session's `auth_time`, so a refresh
 * never upgrades it: the person signs in again and enters a code.
 *
 * A token with `mfa` but no `mfa_since` was issued before this rule (they live at
 * most 60 minutes after the deploy) and keeps the old meaning.
 */
export function mfaPerformed(claims: {
  mfa?: unknown;
  mfa_since?: unknown;
  auth_time?: unknown;
}): boolean {
  if (claims.mfa !== 'true') return false;
  if (claims.mfa_since === undefined) return true;
  const since = Number(claims.mfa_since);
  const authTime = Number(claims.auth_time);
  return Number.isFinite(since) && Number.isFinite(authTime) && authTime >= since;
}
